"use client";

import React, { useEffect } from "react";
import { Loader, CircleCheck } from "lucide-react";
import { toast } from "sonner";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StartYourTransformation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="36531" id="start-your-recovery" className="w-full bg-background" ref={ref}>
      <div className="grid w-full lg:grid-cols-2 min-h-[800px]">
        {}
        <div className={`relative h-64 w-full overflow-hidden lg:h-auto transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="absolute inset-0 bg-primary/20 mix-blend-multiply z-10 !bg-[url()]" />
          <img src="https://solutionsforsleepapnea.com/wp-content/uploads/2026/04/image-3.webp" alt="A peaceful individual sleeping soundly, representing successful sleep apnea treatment and recovery" className="h-full w-full object-cover" />
        </div>

        {}
        <div className="flex flex-col justify-center bg-secondary p-8 lg:p-16 xl:p-24">
          <div className={`mx-auto w-full max-w-lg transition-all duration-1000 delay-300 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <div className="mb-10">
              <h2 className="font-sans text-3xl font-bold tracking-tight text-primary md:text-4xl lg:text-5xl">
                Start Your Recovery
              </h2>
              <p className="mt-4 text-muted-foreground text-lg">
                Ready to take the first step towards better sleep and health? Complete the form below to request your free, no-obligation consultation.
              </p>
            </div>

            <FormProvider formKey="recovery_form_home" formId="recovery_form_home" sectionName="start-your-recovery" defaultValues={{
            name: "",
            email: "",
            phone: "",
            symptoms: "",
            insurance_provider: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            symptoms: {
              required: true
            },
            insurance_provider: {
              required: true
            }
          }} submitText="Send Request" successMessage="Thank you! Your request has been received. One of our specialists will contact you shortly.">
              <QuoteRequestForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function QuoteRequestForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success("Request sent successfully!", {
        description: "We'll be in touch with you shortly to discuss your consultation.",
        icon: <CircleCheck className="h-5 w-5 text-accent" />
      });
    }
  }, [isSubmitted, submitError, resetForm]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-accent/20 bg-accent/5 p-8 text-center animate-in fade-in zoom-in duration-500">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
          <CircleCheck className="h-8 w-8 text-accent" />
        </div>
        <h3 className="mb-2 font-sans text-2xl font-semibold text-primary">
          Request Received
        </h3>
        <p className="mb-6 text-muted-foreground">
          Thank you for reaching out. Our clinical team will review your details
          and get back to you within 24 hours to schedule your visit.
        </p>
        <Button onClick={resetForm} variant="outline" className="w-full sm:w-auto border-primary text-primary hover:bg-primary hover:text-white">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="John Doe" {...field} className="bg-background border-border focus:ring-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background border-border focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" className="bg-background border-border focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="insurance_provider" rules={{
      required: "Insurance provider is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium">Insurance Provider</FormLabel>
            <FormControl>
              <Input type="text" placeholder="e.g., Aetna, Blue Cross, Cigna" className="bg-background border-border focus:ring-primary" {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground/80">
              We accept most major medical insurance plans.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="symptoms" rules={{
      required: "Please describe your symptoms"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium">Describe Your Symptoms</FormLabel>
            <FormControl>
              <Textarea placeholder="Please share any symptoms like snoring, daytime fatigue, or gasping during sleep." className="min-h-[120px] resize-y bg-background border-border focus:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-12 w-full bg-accent text-base font-bold text-white hover:bg-accent/90 shadow-md transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Sending Request...
          </> : "Send Request"}
      </Button>
    </Form>;
}